
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/db';
import LoadingOverlay from '../components/LoadingOverlay';
import './SalesMonthly.css';

// Arabic month names
const arabicMonths = [
    'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
    'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
];

export default function SalesMonthly() {
    const { warehouseId } = useAuth();
    const [loading, setLoading] = useState(true);
    const [reportData, setReportData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');

    // Get current month and year
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth(); // 0-11
    const currentYear = currentDate.getFullYear();
    const monthName = arabicMonths[currentMonth];

    useEffect(() => {
        if (warehouseId) {
            loadReport();
        }
        // eslint-disable-next-line
    }, [warehouseId]);

    async function loadReport() {
        setLoading(true);
        try {
            // Fetch all sales transactions for the current month
            const docs = await api.getSalesTransactionsByMonth(warehouseId, currentYear, currentMonth + 1);

            // Group by item_name and calculate aggregates
            const itemsMap = {};

            docs.forEach(doc => {
                const itemName = doc.item_name;
                const qty = Number(doc.quantity) || 0;
                const totalCost = Number(doc.total_cost) || 0;
                const totalUnitCost = Number(doc.total_unit_cost) || 0;
                const isSale = doc.sale_transaction_code && doc.sale_transaction_code.startsWith('S-');
                const isIncoming = doc.sale_transaction_code && doc.sale_transaction_code.startsWith('SW');

                if (!itemsMap[itemName]) {
                    itemsMap[itemName] = {
                        name: itemName,
                        incoming: 0,    // الوارد
                        outgoing: 0,    // المنصرف
                        revenue: 0,     // الدخل (total sales)
                        profit: 0       // صافي الربح
                    };
                }

                if (isIncoming) {
                    // Incoming (وارد)
                    itemsMap[itemName].incoming += qty;
                } else if (isSale) {
                    // Sale (بيع)
                    itemsMap[itemName].outgoing += qty;
                    itemsMap[itemName].revenue += totalCost;
                    // Profit = Revenue - Total Unit Cost (pre-calculated)
                    itemsMap[itemName].profit += totalCost - totalUnitCost;
                }
            });

            // Convert to array and sort by name
            const data = Object.values(itemsMap).sort((a, b) => a.name.localeCompare(b.name, 'ar'));
            setReportData(data);

        } catch (error) {
            console.error("Failed to load monthly report", error);
            alert("فشل تحميل التقرير الشهري");
        } finally {
            setLoading(false);
        }
    }

    const formatNumber = (num) => Math.round(Number(num) || 0).toLocaleString('en-US');

    // Filter data by search query
    const filteredData = reportData.filter(item =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Calculate totals from filtered data
    const displayTotals = filteredData.reduce((acc, item) => {
        acc.incoming += item.incoming;
        acc.outgoing += item.outgoing;
        acc.revenue += item.revenue;
        acc.profit += item.profit;
        return acc;
    }, { incoming: 0, outgoing: 0, revenue: 0, profit: 0 });

    return (
        <div className="sales-monthly-root" dir="rtl">
            <LoadingOverlay isLoading={loading} />

            <div className="sales-monthly-header">
                <Link to="/sales-home"><button className="back-btn">الرجوع</button></Link>
                <h1>التقرير الشهري لمبيعات ({monthName} {currentYear})</h1>
            </div>

            <div className="sales-monthly-container">
                <div className="search-box">
                    <label>بحث عن صنف:</label>
                    <input
                        type="text"
                        placeholder="اكتب اسم الصنف للبحث..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>

                {filteredData.length === 0 && !loading ? (
                    <p className="no-data">{searchQuery ? 'لم يتم العثور على نتائج للبحث' : 'لا توجد بيانات لهذا الشهر'}</p>
                ) : (
                    <table className="report-table">
                        <thead>
                            <tr>
                                <th>الصنف</th>
                                <th>الوارد</th>
                                <th>المنصرف</th>
                                <th>الدخل</th>
                                <th>صافي الربح</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredData.map((item, idx) => (
                                <tr key={idx}>
                                    <td>{item.name}</td>
                                    <td>{item.incoming}</td>
                                    <td>{item.outgoing}</td>
                                    <td>{formatNumber(item.revenue)}</td>
                                    <td className={item.profit >= 0 ? 'profit-positive' : 'profit-negative'}>
                                        {formatNumber(item.profit)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="totals-row">
                                <td><strong>الإجمالي</strong></td>
                                <td><strong>{displayTotals.incoming}</strong></td>
                                <td><strong>{displayTotals.outgoing}</strong></td>
                                <td><strong>{formatNumber(displayTotals.revenue)}</strong></td>
                                <td className={displayTotals.profit >= 0 ? 'profit-positive' : 'profit-negative'}>
                                    <strong>{formatNumber(displayTotals.profit)}</strong>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                )}
            </div>
        </div>
    );
}
